import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentProps } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { Select } from "components/ui/select/Select"
import type { TEref } from "types/elevationReferences.type"
import style from "./ConnectToElevationModal.module.scss"

type Props = {
	isOpen: boolean
	onClose: () => void
	onConfirm: (erefId: number) => void
	/** Elevation references on the selected objects' page — the only valid connection targets. */
	erefs: TEref[]
	isPending?: boolean
	title?: string
	description?: string
	confirmLabel?: string
	// When provided, overrides the default WebViewer shadow-root portal logic.
	getContainer?: ComponentProps<typeof Modal>["getContainer"]
}

/**
 * Confirmation modal for connecting / moving furniture to an elevation reference (Figma
 * "Connect object to elevation"). Single-select only — multi-selection is intentionally
 * disabled. Reuses the app `Select` (with `renderInPlace` so its popup stays inside the shadow
 * DOM) and portals into the Apryse shadow root the same way `ConfirmModal` does.
 */
export const ConnectToElevationModal = ({
	isOpen,
	onClose,
	onConfirm,
	erefs,
	isPending = false,
	title = "Connect object to elevation",
	description = "Select an elevation on this page to link this object to.",
	confirmLabel = "Connect",
	getContainer,
}: Props) => {
	const anchorRef = useRef<HTMLDivElement | null>(null)
	const portalRef = useRef<HTMLDivElement | null>(null)
	const [selectedErefId, setSelectedErefId] = useState<number | null>(null)

	useEffect(() => {
		return () => {
			portalRef.current?.remove()
			portalRef.current = null
		}
	}, [])

	// Reset the choice whenever the modal (re)opens so a prior selection doesn't leak in.
	useEffect(() => {
		if (isOpen) setSelectedErefId(null)
	}, [isOpen])

	const options = useMemo(() => {
		const sorted = erefs.slice().sort((a, b) => {
			const an = Number(a.elevation_number)
			const bn = Number(b.elevation_number)
			const aValid = Number.isFinite(an)
			const bValid = Number.isFinite(bn)
			if (aValid && bValid) return an - bn
			if (aValid) return -1
			if (bValid) return 1
			return String(a.elevation_number ?? "").localeCompare(String(b.elevation_number ?? ""))
		})
		return sorted.map((eref) => ({
			label: `${eref.elevation_number ?? "—"} — ${eref.page_name ?? "Unknown"}`,
			value: eref.id,
		}))
	}, [erefs])

	const defaultGetContainer = useCallback((): HTMLElement => {
		if (portalRef.current?.isConnected) return portalRef.current
		const root = anchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: ((document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body)
		const container = document.createElement("div")
		container.setAttribute("data-portal", "connect-elevation-modal")
		host.appendChild(container)
		portalRef.current = container
		return container
	}, [])

	const resolvedGetContainer = getContainer !== undefined ? getContainer : defaultGetContainer

	const handleConfirm = () => {
		if (selectedErefId == null || isPending) return
		onConfirm(selectedErefId)
	}

	return (
		<>
			<div ref={anchorRef} style={{ display: "none" }} aria-hidden />
			<Modal isModalOpen={isOpen} setModalClose={onClose} width={456} getContainer={resolvedGetContainer}>
				<div className={style.body} data-testid="connect-elevation-modal">
					<div className={style.header}>
						<h3 className={style.title}>{title}</h3>
						<p className={style.description}>{description}</p>
					</div>
					<div className={style.field}>
						<span className={style.fieldLabel}>Elevation reference</span>
						<Select
							variant="outlined"
							options={options}
							value={selectedErefId}
							onChange={(next) => setSelectedErefId(typeof next === "number" ? next : null)}
							placeholder="Select elevation"
							showSearch
							optionFilterProp="label"
							allowClear
							renderInPlace
						/>
					</div>
					<div className={style.actions}>
						<button type="button" className={style.cancelButton} onClick={onClose} disabled={isPending}>
							Cancel
						</button>
						<button
							type="button"
							className={style.confirmButton}
							onClick={handleConfirm}
							disabled={isPending || selectedErefId == null}
						>
							{confirmLabel}
						</button>
					</div>
				</div>
			</Modal>
		</>
	)
}
